import { computed, ref, type Ref } from 'vue';

/**
 * Composable для throttle обновлений активного пункта меню.
 * 
 * Ограничивает частоту обновлений активного пункта при быстрых изменениях
 * из внешних источников (например, при скроллинге контента), но сохраняет
 * мгновенный отклик на клики пользователя.
 * 
 * @param internalActiveId - Реактивная ссылка на текущий активный ID
 * @param onActiveChange - Callback для применения изменения активного элемента
 * @param throttleMs - Интервал throttle в миллисекундах (по умолчанию 100ms)
 * 
 * @example
 * ```typescript
 * const { setActiveThrottled, cancel } = useActiveItemThrottle(
 *   internalActiveId,
 *   (id) => {
 *     // Обновить индикатор и scroll-to-active
 *     rafScheduler.schedule('update-indicator', updateIndicator, 'high');
 *     rafScheduler.schedule('scroll-to-active', () => scrollToActiveItem(id), 'normal');
 *   },
 *   100
 * );
 * 
 * // External update - с throttle
 * watch(() => props.modelValue, (newValue) => {
 *   setActiveThrottled(newValue, false);
 * });
 * 
 * // User click - без throttle
 * const handleClick = (item) => {
 *   setActiveThrottled(item.id, true);
 * };
 * 
 * // Cleanup
 * onUnmounted(() => {
 *   cancel();
 * });
 * ```
 */
export function useActiveItemThrottle(
  internalActiveId: Ref<string | number | null>,
  onActiveChange: (id: string | number | null) => void,
  throttleMs: number = 100
) {
  const throttleTimer = ref<ReturnType<typeof setTimeout> | null>(null);
  const lastExecutionTime = ref<number>(0);
  let pendingId: string | number | null = null;

  /**
   * Установить активный элемент с опциональным throttle.
   * 
   * @param id - ID элемента для установки как активного
   * @param immediate - Если true, изменение применяется немедленно (для user clicks).
   *                    Если false, изменение throttle (для external updates).
   */
  const setActiveThrottled = (
    id: string | number | null,
    immediate: boolean = false
  ): void => {
    // User-initiated изменения применяются немедленно
    if (immediate) {
      // Отменить pending throttled update
      if (throttleTimer.value !== null) {
        clearTimeout(throttleTimer.value);
        throttleTimer.value = null;
      }
      applyActiveChange(id);
      lastExecutionTime.value = Date.now();
      pendingId = null;
      return;
    }

    // External изменения throttle
    pendingId = id;
    
    const now = Date.now();
    const timeSinceLastExecution = now - lastExecutionTime.value;

    // Если прошло достаточно времени - выполняем сразу
    if (timeSinceLastExecution >= throttleMs) {
      applyActiveChange(id);
      lastExecutionTime.value = now;
      pendingId = null;
      return;
    }

    // Иначе планируем выполнение через оставшееся время
    if (throttleTimer.value === null) {
      const remainingTime = throttleMs - timeSinceLastExecution;
      throttleTimer.value = setTimeout(() => {
        if (pendingId !== null) {
          applyActiveChange(pendingId);
          lastExecutionTime.value = Date.now();
          pendingId = null;
        }
        throttleTimer.value = null;
      }, remainingTime);
    }
  };

  /**
   * Применить изменение активного элемента.
   * Обновляет internalActiveId и вызывает callback для визуальных обновлений.
   * 
   * @param id - ID элемента для установки как активного
   */
  const applyActiveChange = (id: string | number | null): void => {
    // Пропустить если значение не изменилось
    if (internalActiveId.value === id) return;
    
    internalActiveId.value = id;
    onActiveChange(id);
  };

  /**
   * Отменить pending throttled update.
   * Используется при unmount компонента для предотвращения memory leaks.
   */
  const cancel = (): void => {
    if (throttleTimer.value !== null) {
      clearTimeout(throttleTimer.value);
      throttleTimer.value = null;
      pendingId = null;
    }
  };

  /**
   * Computed флаг наличия pending throttled update.
   * Полезен для отладки и тестирования.
   */
  const hasPending = computed(() => throttleTimer.value !== null);

  return {
    setActiveThrottled,
    cancel,
    hasPending
  };
}
